import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { has, isEqual } from 'lodash';
import { useParams } from 'react-router-dom';
import {
  InputAdornment,
  InputLabel,
  OutlinedInput,
  FormControl,
} from '@material-ui/core';

import { useSnackBar } from 'context/SnackBarContext';
import Dialog from 'components/ui/Dialog';
import Form from 'components/ui/Form';
import {
  FormSectionTitle,
  GridContainer,
  StyledField,
  StyledHelperText,
} from 'styles/form';
import AddressFormFields from 'components/general/AddressFormFields';
import GeneralInfoFormFields from 'components/general/GeneralInfoFormFields';
import { createAdmin, deleteAdmin, showAdmin, updateAdmin } from 'api/admin';
import { revertPhoneMask } from 'helpers/revert-phone-mask';
import { initialValues, validationSchema } from '../admin.config';

const Admin = ({
  modalOpen,
  handleModalClose,
  blockRouteChange,
  setBlockRouteChange,
}) => {
  const { id } = useParams();
  const { setMessage, setOpenSnackBar, setSeverity } = useSnackBar();

  const [admin, setAdmin] = useState();

  const isAddModal = id === 'add';

  useEffect(async () => {
    if (id && !isAddModal) {
      const { data } = await showAdmin(id);
      setAdmin(data);
    }
  }, []);

  const handleSave = async (values) => {
    if (admin) {
      try {
        await updateAdmin(id, values);
        setMessage('Saved changes');
        setSeverity('success');
      } catch (error) {
        console.error(error);
        setMessage('Something went wrong. Failed to save changes.');
        setSeverity('error');
      }
    } else {
      try {
        await createAdmin(values);
        setMessage('Admin created');
        setSeverity('success');
      } catch (error) {
        console.error(error);
        setMessage('Something went wrong. Failed to create admin.');
        setSeverity('error');
      }
    }

    setOpenSnackBar(true);
    handleModalClose(true);
  };

  const handleDelete = async () => {
    try {
      await deleteAdmin(id);
      setMessage('Admin deleted');
      setSeverity('success');
    } catch (error) {
      console.error(error);
      setMessage('Something went wrong. Failed to delete admin.');
      setSeverity('error');
    }

    setOpenSnackBar(true);
    handleModalClose(true);
  };

  return (
    <Formik
      initialValues={initialValues(admin, isAddModal)}
      validationSchema={validationSchema(isAddModal)}
      enableReinitialize
      onSubmit={handleSave}
    >
      {({
        values,
        errors,
        setFieldValue,
        handleChange,
        submitForm,
        touched,
      }) => {
        const initValues = initialValues(admin, isAddModal);
        const {
          firstName,
          lastName,
          email,
          phone,
          password,
          passwordConfirmation,
          address,
          city,
          state,
          postalCode,
          country,
          rate,
        } = values;

        const unsavedChange = !isEqual(
          { ...values, rate: Number(rate), phone: revertPhoneMask(phone) },
          { ...initValues, rate: Number(initValues.rate) },
        );

        if (unsavedChange) {
          setBlockRouteChange(true);
        } else {
          setBlockRouteChange(false);
        }

        return (
          <Dialog
            open={modalOpen}
            handleClose={handleModalClose}
            submitForm={submitForm}
            typeText="Admin"
            handleDelete={handleDelete}
            blockRouteChange={blockRouteChange}
          >
            <FormSectionTitle>Admin</FormSectionTitle>
            <Form fullWidth onSubmit={submitForm}>
              <GridContainer>
                <GeneralInfoFormFields
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  phone={phone}
                  password={password}
                  passwordConfirmation={passwordConfirmation}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  showPasswordFields={id === 'add'}
                />

                <AddressFormFields
                  address={address}
                  country={country}
                  city={city}
                  state={state}
                  postalCode={postalCode}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  handleChange={handleChange}
                />

                <FormControl fullWidth variant="outlined">
                  <StyledField variant="outlined">
                    <InputLabel
                      htmlFor="outlined-adornment-amount"
                      error={touched.rate && has(errors, 'rate')}
                    >
                      Rate
                    </InputLabel>
                    <OutlinedInput
                      onChange={handleChange}
                      error={touched.rate && has(errors, 'rate')}
                      helperText={touched.rate && errors.rate}
                      name="rate"
                      value={rate}
                      required
                      startAdornment={
                        <InputAdornment position="start">$</InputAdornment>
                      }
                      labelWidth={40}
                      autoComplete="chrome-off"
                    />
                  </StyledField>
                  {errors.rate && touched.rate && (
                    <StyledHelperText error={has(errors, 'rate')}>
                      field is required
                    </StyledHelperText>
                  )}
                </FormControl>
              </GridContainer>
            </Form>
          </Dialog>
        );
      }}
    </Formik>
  );
};

export default Admin;
